import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, ChevronDown } from "lucide-react";
import { useCart, useAuth, useSite } from "../lib/store";
import { money, img } from "../lib/api";

export const NAV = [
  ["/", "Domů"],
  ["/eshop", "E-shop"],
  ["/kategorie/materialy-zdarma", "Zdarma"],
  ["/darkove-poukazy", "Dárkové poukazy"],
  ["/jak-to-funguje", "Jak to funguje"],
  ["/o-nas", "O nás"],
  ["/kontakt", "Kontakt"],
];

function SearchBox({ onDone }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    nav(`/eshop?q=${encodeURIComponent(q)}`);
    onDone?.();
  };
  return (
    <form onSubmit={submit} className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
      <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat materiál…" className="w-full lg:w-44 xl:w-56 rounded-full border-2 border-[#f3dbe6] bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-[var(--pink)] transition-colors" />
    </form>
  );
}

function CatLink({ c, onClick, testid }) {
  return (
    <Link to={`/kategorie/${c.slug}`} onClick={onClick} data-testid={testid} className="group/cat flex items-center gap-3 rounded-2xl px-2 py-1.5 hover:bg-[var(--blush)] transition-colors">
      <img src={img(c.image)} alt="" loading="lazy" className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm group-hover/cat:scale-110 transition-transform" />
      <span className="text-sm font-semibold leading-tight group-hover/cat:text-[var(--pink-dark)]">{c.name}</span>
    </Link>
  );
}

export default function Header() {
  const { count, total } = useCart();
  const { user } = useAuth();
  const { site } = useSite();
  const cats = site?.categories || [];
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const link = ({ isActive }) => `relative whitespace-nowrap px-1 py-2 text-[15px] font-semibold transition-colors ${isActive ? "text-[var(--pink-dark)]" : "text-[var(--ink)] hover:text-[var(--pink)]"}`;
  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#f6e6ee]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 shrink-0">
          <img src="/logo.png" alt="Materiály pro školky" className="h-14 w-auto" />
          <span className="font-display leading-none hidden sm:block">
            <span className="block text-lg">Materiály</span>
            <span className="block text-lg text-[var(--pink)]">pro školky</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-3 xl:gap-5 mx-auto">
          {NAV.map(([to, label]) =>
            to === "/eshop" ? (
              <div key={to} className="relative group" data-testid="nav-eshop-dropdown">
                <NavLink to={to} className={link} data-testid="nav--eshop">
                  <span className="inline-flex items-center gap-1">{label}<ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" /></span>
                </NavLink>
                <div className="absolute -left-24 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-[opacity,transform] duration-200">
                  <div className="w-[min(720px,calc(100vw-2rem))] bg-white rounded-[28px] border border-[#f6e3ec] shadow-[0_30px_60px_-25px_rgba(212,86,140,.4)] p-5" data-testid="nav-eshop-menu">
                    <div className="grid grid-cols-3 gap-1">
                      {cats.map((c) => <CatLink key={c.id} c={c} testid={`nav-cat-${c.slug}`} />)}
                    </div>
                    <Link to="/eshop" className="mt-3 flex items-center justify-center gap-2 rounded-2xl bg-[var(--blush)] py-3 text-sm font-bold text-[var(--pink-dark)] hover:bg-[var(--pink-soft)] transition-colors" data-testid="nav-eshop-all">Zobrazit všechny materiály</Link>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={to} to={to} end={to === "/"} className={link} data-testid={`nav-${to.replace(/\W+/g, "-")}`}>
                {label}
              </NavLink>
            )
          )}
        </nav>
        <div className="ml-auto lg:ml-0 flex items-center gap-2">
          <div className="hidden xl:block"><SearchBox /></div>
          <Link to={user ? "/ucet" : "/prihlaseni"} data-testid="header-account-link" className="h-11 px-3 flex items-center gap-2 rounded-full hover:bg-[var(--pink-soft)] transition-colors text-sm font-bold" aria-label="Účet">
            <User className="w-5 h-5" /><span className="hidden 2xl:inline whitespace-nowrap">{user ? "Můj účet" : "Přihlásit"}</span>
          </Link>
          <Link to="/kosik" data-testid="header-cart-link" className="relative h-11 pl-3 pr-4 flex items-center gap-2 rounded-full bg-[var(--ink)] text-white hover:bg-[var(--pink)] transition-colors text-sm font-bold" aria-label="Košík">
            <ShoppingBag className="w-5 h-5" /><span className="hidden sm:inline whitespace-nowrap">{money(total)}</span>
            {count > 0 && <span data-testid="header-cart-count" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[var(--sun)] text-[var(--ink)] text-[11px] font-extrabold grid place-items-center">{count}</span>}
          </Link>
          <button className={`lg:hidden burger ${open ? "open" : ""} w-11 h-11 rounded-full bg-[var(--pink-soft)] hover:bg-[#f7d6e5] flex flex-col items-end justify-center gap-[4px] px-3 transition-colors`} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            <span className="w-full" /><span className="w-2/3" /><span className="w-full" />
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-[#f6e6ee] bg-white/95 backdrop-blur-xl px-6 py-6 space-y-1 shadow-[0_20px_40px_-20px_rgba(212,86,140,.3)]" data-testid="mobile-menu">
          <div className="mb-4"><SearchBox onDone={() => setOpen(false)} /></div>
          {NAV.map(([to, label], i) =>
            to === "/eshop" ? (
              <div key={to}>
                <button onClick={() => setShopOpen(!shopOpen)} data-testid="mobile-eshop-toggle" className="w-full flex items-center justify-between rounded-2xl px-4 py-3 font-display text-xl hover:bg-[var(--blush)] transition-colors">
                  {label}<ChevronDown className={`w-5 h-5 text-[var(--teal)] transition-transform ${shopOpen ? "rotate-180" : ""}`} />
                </button>
                {shopOpen && (
                  <div className="ml-2 pl-3 border-l-2 border-dashed border-[#f0cfe0] space-y-0.5 pb-2" data-testid="mobile-eshop-categories">
                    <Link to="/eshop" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm font-bold text-[var(--pink-dark)]">Všechny materiály</Link>
                    {cats.map((c) => <CatLink key={c.id} c={c} onClick={() => setOpen(false)} testid={`mobile-cat-${c.slug}`} />)}
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} style={{ animationDelay: `${i * 40}ms` }} className={({ isActive }) => `flex items-center justify-between rounded-2xl px-4 py-3 font-display text-xl transition-colors animate-in fade-in slide-in-from-top-2 ${isActive ? "bg-[var(--pink-soft)] text-[var(--pink-dark)]" : "hover:bg-[var(--blush)]"}`}>
                {label}<span className="w-2 h-2 rounded-full" style={{ background: ["var(--pink)", "var(--teal)", "var(--sun)", "var(--lilac)"][i % 4] }} />
              </NavLink>
            )
          )}
        </div>
      )}
    </header>
  );
}
